import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import RenderHtml from 'react-native-render-html';
import {
  FacebookShareButton,
  LinkedinShareButton,
  RedditShareButton,
  TwitterShareButton,
} from 'react-share';
import { COLORS, SHADOWS } from '../../constants/theme';
import { parseDescriptionToHtml, getImageUrl } from './utils';
import MediaComponent from './mediacomponent';
import moment from 'moment';

const HubitaLogo = require('../../assets/images/Logo/HubitaWeb.png');

const MediaDetails = (props) => {
  const [mediaItem, setMediaItem] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const postId = props.selectedPost || props?.route?.params?.data;

  useEffect(() => {
    if (postId) {
      setLoading(true);
      ajax
        .get(Urls.GET_POST_DETAILS + postId)
        .then(({ data }) => {
          if (data.status) {
            setMediaItem(data.payload);
          }
        })
        .catch((error) => {
          console.log('error fetching media details', error);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    ajax
      .get(Urls.GET_POST + 'BLOG')
      .then(({ data }) => {
        if (data.status) {
          const list = data?.data?.mediahub || data?.payload || [];
          setRelatedPosts(list.filter((item) => String(item.id) !== String(postId)).slice(0, 3));
        }
      })
      .catch(() => {});
  }, [postId]);

  const articleUrl = typeof window !== 'undefined'
    ? window.location.href
    : `https://dev.hubita.co.uk/LandlordMediaDetails?data=${mediaItem?.id || ''}`;

  const formattedHtml = parseDescriptionToHtml(mediaItem?.description);
  const formattedDate = mediaItem?.createdAt
    ? moment(mediaItem.createdAt).format('DD MMM YYYY')
    : 'Hubita Media';

  // Extract detail image with fallback to cover_image, file, or image
  const heroImageSrc = getImageUrl(
    mediaItem?.detail_image || mediaItem?.cover_image || mediaItem?.file || mediaItem?.image
  );

  return (
    <View style={styles.root}>
      {/* Breadcrumb Header */}
      <View style={[styles.breadcrumbBar, isMobile && styles.breadcrumbBarMobile]}>
        <TouchableOpacity
          onPress={() => props.navigation?.navigate('MediaHub')}
          style={styles.backBtn}
          activeOpacity={0.8}
        >
          <Text style={styles.breadText}>Back / Media Hub</Text>
        </TouchableOpacity>
        <Text style={styles.breadSep}>/</Text>
        <Text style={styles.breadActive} numberOfLines={1}>
          {mediaItem?.title || 'Article Details'}
        </Text>
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={[
          styles.scrollContent,
          isMobile && styles.scrollContentMobile,
        ]}
        showsVerticalScrollIndicator={true}
      >
        {loading ? (
          <View style={styles.loaderWrap}>
            <ActivityIndicator size="large" color="#6C2BD9" />
          </View>
        ) : mediaItem ? (
          <View>
            {/* Main Article Details Card */}
            <View style={[styles.articleCard, isMobile && styles.articleCardMobile]}>
              {/* Category Tag & Date Meta */}
              <View style={styles.metaRow}>
                <View style={styles.categoryBadge}>
                  <Text style={styles.categoryText}>Landlord Hub</Text>
                </View>
                <Text style={styles.dateText}>{formattedDate}</Text>
              </View>

              {/* Title */}
              <Text style={styles.title}>{mediaItem.title}</Text>

              {/* Share Row */}
              <View style={styles.shareRow}>
                <Text style={styles.shareLabel}>Share Article:</Text>
                <View style={styles.shareButtons}>
                  <FacebookShareButton url={articleUrl}>
                    <Image
                      source={require('../../assets/images/fb.png')}
                      style={styles.socialIcon}
                    />
                  </FacebookShareButton>
                  <TwitterShareButton url={articleUrl}>
                    <Image
                      source={require('../../assets/images/x.png')}
                      style={styles.socialIcon}
                    />
                  </TwitterShareButton>
                  <LinkedinShareButton url={articleUrl}>
                    <Image
                      source={require('../../assets/images/linkdin.png')}
                      style={styles.socialIcon}
                    />
                  </LinkedinShareButton>
                  <RedditShareButton url={articleUrl}>
                    <Image
                      source={require('../../assets/images/reddit.png')}
                      style={styles.socialIcon}
                    />
                  </RedditShareButton>
                </View>
              </View>

              {/* Featured Image or Hubita Logo Placeholder */}
              {heroImageSrc ? (
                <View style={styles.imageWrap}>
                  <Image source={{ uri: heroImageSrc }} style={styles.heroImage} />
                </View>
              ) : (
                <View style={styles.imagePlaceholderWrap}>
                  <Image
                    source={HubitaLogo}
                    style={styles.heroLogoPlaceholder}
                    resizeMode="contain"
                  />
                </View>
              )}

              {/* Article Content */}
              <View style={styles.bodyContent}>
                <RenderHtml
                  contentWidth={width - 80}
                  source={{ html: formattedHtml }}
                  tagsStyles={{
                    p: { fontSize: 15, lineHeight: 24, color: '#374151', marginBottom: 14 },
                    h1: { fontSize: 24, fontWeight: '700', color: '#111827', marginBottom: 12 },
                    h2: { fontSize: 20, fontWeight: '700', color: '#111827', marginBottom: 10 },
                    h3: { fontSize: 17, fontWeight: '700', color: '#111827', marginBottom: 8 },
                    li: { fontSize: 15, lineHeight: 24, color: '#374151' },
                  }}
                />
              </View>

              {/* Bottom Back Button */}
              <TouchableOpacity
                style={styles.bottomBackBtn}
                onPress={() => props.navigation?.navigate('MediaHub')}
                activeOpacity={0.8}
              >
                <Ionicons name="arrow-back" size={16} color="#6C2BD9" />
                <Text style={styles.bottomBackText}>Back to Media Hub</Text>
              </TouchableOpacity>
            </View>

            {/* Related Articles Section */}
            {relatedPosts && relatedPosts.length > 0 ? (
              <View style={styles.relatedSection}>
                <Text style={styles.relatedTitle}>Related Articles</Text>
                <View style={styles.relatedGrid}>
                  {relatedPosts.map((rel, idx) => (
                    <View
                      key={`rel-${idx}`}
                      style={{ width: isMobile ? '100%' : '31.8%' }}
                    >
                      <MediaComponent
                        coverImage={rel.cover_image || rel.file || rel.image}
                        title={rel.title}
                        description={rel.description}
                        date={rel.createdAt ? moment(rel.createdAt).format('MMM DD, YYYY') : 'Article'}
                        category="Landlord Hub"
                        isMobile={isMobile}
                        onPress={() => {
                          props.navigation?.navigate('LandlordMediaDetails', {
                            data: rel.id,
                          });
                        }}
                      />
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
          </View>
        ) : (
          <View style={styles.loaderWrap}>
            <Text style={{ fontSize: 14, color: '#9CA3AF' }}>Article not found.</Text>
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#F5F7FB',
    overflow: 'hidden',
  },
  breadcrumbBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 24,
    paddingTop: 14,
    paddingBottom: 4,
    flexShrink: 0,
  },
  breadcrumbBarMobile: {
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 2,
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  breadSep: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  breadActive: {
    fontSize: 12,
    color: '#6C2BD9',
    fontWeight: '600',
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 40,
  },
  scrollContentMobile: {
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 50,
  },
  loaderWrap: {
    paddingVertical: 60,
    alignItems: 'center',
  },
  articleCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 28,
    borderWidth: 1,
    borderColor: '#F3F4F6',
    ...SHADOWS.sm,
  },
  articleCardMobile: {
    padding: 16,
    borderRadius: 14,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  categoryBadge: {
    backgroundColor: '#F0E8FF',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
  },
  categoryText: {
    color: '#6C2BD9',
    fontSize: 11,
    fontWeight: '700',
  },
  dateText: {
    fontSize: 12,
    color: '#6B7280',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
    lineHeight: 32,
    marginBottom: 16,
  },
  shareRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  shareLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6B7280',
  },
  shareButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  socialIcon: {
    width: 28,
    height: 28,
    resizeMode: 'contain',
  },
  imageWrap: {
    width: '100%',
    height: 360,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 24,
    backgroundColor: '#F3F4F6',
  },
  imagePlaceholderWrap: {
    width: '100%',
    height: 220,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 24,
    backgroundColor: '#F0E8FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroLogoPlaceholder: {
    width: 180,
    height: 60,
  },
  heroImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  bodyContent: {
    width: '100%',
    marginBottom: 20,
  },
  bottomBackBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#F0E8FF',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    alignSelf: 'flex-start',
    marginTop: 10,
  },
  bottomBackText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#6C2BD9',
  },
  relatedSection: {
    marginTop: 32,
  },
  relatedTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginBottom: 16,
  },
  relatedGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
});

export default drawerHoc({
  Component: MediaDetails,
  showBackButton: true,
  noScroll: true,
});
